import { useContext, useRef, useState } from "react";
import { Bell, Trash2 } from "lucide-react";
import AuthContext from "../context/AuthContext";
import EventDetailHeader from "./EventDetailHeader";
import { GuestList, GuestSummary, guestStats } from "./EventGuests";
import { REMINDER_OPTIONS, effectiveOccurrence } from "../utils/eventDomain";
import { isNotFound } from "../utils/eventErrors";
import { fetchOccurrence } from "../api/EventAPI";
import { answerOccurrence, removeOccurrenceParticipation } from "../utils/occurrenceActions";
import {
  REMOVE_EVENT_CONFIRMATION,
  occurrenceOnlyDetailLayout,
  reminderEdit,
} from "../utils/occurrenceParticipation";

const ANSWER_LABELS = { going: "Going", declined: "Decline" };

// Detail of an occurrence the viewer was invited to on its own
// (access_scope "occurrence"; mirrors iOS OccurrenceDetailView). They can see
// this date but not its series — GET /events/<id>/ is a 404 for them — so
// everything comes from the occurrence (GET /event-occurrences/<id>/): its
// effective values, their participation and the guest list the backend
// allows. It reads as a standalone event: nothing refers to a series (no
// recurrence, series RSVP or invitation, no "this date" wording), and no
// organizer action is offered.
//   pending   Going / Decline (this occurrence's endpoint)
//   going     no RSVP card; Reminder; Remove Event (confirmed)
//   declined, canceled  read-only
//
// `occurrence` is owned by EventFocusModal (onOccurrenceChange replaces it).
export default function OccurrenceOnlyDetail({ occurrence, onOccurrenceChange, onClose }) {
  const { token } = useContext(AuthContext);
  const [answering, setAnswering] = useState(null);
  const [answerError, setAnswerError] = useState("");
  const [reminderPending, setReminderPending] = useState(false);
  const [reminderError, setReminderError] = useState("");
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removalError, setRemovalError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [guestListStatus, setGuestListStatus] = useState(null);
  // Synchronous guard against double-submitting the removal.
  const removalInFlight = useRef(false);

  const details = effectiveOccurrence({ occurrence });
  const layout = occurrenceOnlyDetailLayout(occurrence);
  const isGoing = occurrence.my_rsvp_status === "going";
  const showsReminder = isGoing && occurrence.status !== "canceled";
  // The backend lists guests only to a viewer who's going.
  const stats = isGoing ? guestStats(occurrence.rsvps) : [];

  // The fresh occurrence (participation and guest list) after a change; a
  // failed re-read keeps the confirmed result already shown.
  const reread = async () => {
    try {
      onOccurrenceChange(await fetchOccurrence(occurrence.id, token));
    } catch (err) {
      if (isNotFound(err)) setUnavailable(true);
    }
  };

  const handleAnswer = async (status) => {
    if (answering) return;
    setAnswering(status);
    setAnswerError("");
    try {
      const { occurrence: updated } = await answerOccurrence(occurrence, { status }, token);
      onOccurrenceChange(updated);
      // Going may now show who else is going.
      await reread();
    } catch (err) {
      console.error("Failed to answer occurrence invitation", err);
      if (isNotFound(err)) setUnavailable(true);
      else setAnswerError("Couldn't update your RSVP. Please try again.");
    } finally {
      setAnswering(null);
    }
  };

  // This occurrence's reminder only; the status is left out of the request.
  const handleReminderChange = async (option) => {
    const edit = reminderEdit({ occurrence, option });
    if (reminderPending || !edit) return;
    setReminderPending(true);
    setReminderError("");
    try {
      const { occurrence: updated } = await answerOccurrence(occurrence, { reminder: edit.reminder }, token);
      onOccurrenceChange(updated);
    } catch (err) {
      console.error("Failed to update reminder", err);
      if (isNotFound(err)) setUnavailable(true);
      else setReminderError("Couldn't update your reminder. Please try again.");
    } finally {
      setReminderPending(false);
    }
  };

  // Remove Event: the viewer's participation only — the organizer's event and
  // other guests' RSVPs stay. Once the DELETE succeeds the occurrence is
  // dropped from Calendar, Home, Next Event and the inbox (announced by
  // removeOccurrenceParticipation) and the detail closes, with no follow-up
  // request. A failure changes nothing.
  const handleRemove = async () => {
    if (removalInFlight.current) return;
    removalInFlight.current = true;
    setRemoving(true);
    setRemovalError("");
    try {
      await removeOccurrenceParticipation(occurrence, token);
      onClose?.();
    } catch (err) {
      console.error("Failed to remove occurrence", err);
      setRemovalError(
        isNotFound(err) ? "This event is no longer available." : "Couldn't remove this event. Please try again."
      );
    } finally {
      removalInFlight.current = false;
      setRemoving(false);
    }
  };

  const panelClass = "bg-white rounded-lg shadow-lg w-full max-w-md p-5";

  if (unavailable) {
    return (
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-2">{details.title}</h2>
        <p className="text-sm text-gray-600">This event is no longer available.</p>
      </div>
    );
  }

  if (confirmingRemoval) {
    const c = REMOVE_EVENT_CONFIRMATION;
    return (
      <div
        role="alertdialog"
        aria-labelledby="occurrence-removal-title"
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="occurrence-removal-title" className="text-base font-semibold mb-1">
          {c.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4">{c.message}</p>
        {removalError && <p className="text-xs text-red-600 mb-3">{removalError}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={removing}
            onClick={() => setConfirmingRemoval(false)}
            className="flex-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            {c.dismissLabel}
          </button>
          <button
            type="button"
            disabled={removing}
            onClick={handleRemove}
            className="flex-1 rounded-full bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          >
            {removing ? "Working…" : c.confirmLabel}
          </button>
        </div>
      </div>
    );
  }

  if (guestListStatus) {
    return (
      <GuestList
        stat={stats.find((s) => s.status === guestListStatus)}
        status={guestListStatus}
        onBack={() => setGuestListStatus(null)}
      />
    );
  }

  return (
    <div
      className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <EventDetailHeader details={details} hostedBy={occurrence.creator?.username ?? null} />

      {details.isCanceled && (
        <p className="text-xs text-gray-500 mb-4">This event was canceled and can no longer be changed.</p>
      )}

      <GuestSummary stats={stats} onOpen={setGuestListStatus} />

      {layout.rsvpStatus && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase text-gray-500 mb-2">Your RSVP</p>
          <p className="text-sm text-gray-700 mb-2">{layout.rsvpStatus}</p>
          {layout.answers.length > 0 && (
            <div className="flex gap-2">
              {layout.answers.map((status) => (
                <button
                  key={status}
                  type="button"
                  disabled={Boolean(answering)}
                  onClick={() => handleAnswer(status)}
                  className={`flex-1 rounded-full border px-3 py-1.5 text-sm disabled:opacity-50 ${
                    status === "going"
                      ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-600"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {answering === status ? "Working…" : ANSWER_LABELS[status]}
                </button>
              ))}
            </div>
          )}
          {answerError && <p className="text-xs text-red-600 mt-1">{answerError}</p>}
        </div>
      )}

      {showsReminder && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <Bell size={14} className="text-gray-500" />
            <span>Reminder</span>
            <select
              value={occurrence.my_reminder ?? "none"}
              disabled={reminderPending}
              onChange={(e) => handleReminderChange(e.target.value)}
              className="ml-auto border rounded px-2 py-1 text-sm disabled:opacity-50"
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {reminderError && <p className="text-xs text-red-600 mt-1">{reminderError}</p>}
        </div>
      )}

      {layout.offersRemoveEvent && (
        <div className="pt-3 mt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              setRemovalError("");
              setConfirmingRemoval(true);
            }}
            className="flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border border-red-300 text-red-600 hover:bg-red-50"
          >
            <Trash2 size={14} /> Remove Event
          </button>
        </div>
      )}
    </div>
  );
}
